/* =====================================================================
 * @auw/i18n — locale-aware date / number formatters.
 *
 * All formatters pull the current locale from the singleton i18next
 * instance exported by `./index`. Hosts therefore get consistent
 * date/number rendering as soon as `i18n.changeLanguage()` fires,
 * without having to pass the locale down through React context.
 * ===================================================================== */

import i18n from './bootstrap';
import { toIntlLocale, type AppLocale } from './resolveAppLocale';

function currentIntlLocale(): string {
  return toIntlLocale(i18n.language as AppLocale);
}

export function formatDate(date: Date | number | string, options?: Intl.DateTimeFormatOptions): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString(currentIntlLocale(), options);
}

export function formatDateTime(date: Date | number | string, options?: Intl.DateTimeFormatOptions): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString(currentIntlLocale(), options);
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return value.toLocaleString(currentIntlLocale(), options);
}

export function formatRelativeTime(date: Date | number | string): string {
  const d = date instanceof Date ? date : new Date(date);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return i18n.t('time.justNow');
  if (diffMin < 60) return i18n.t('time.minutesAgo', { count: diffMin });
  if (diffHour < 24) return i18n.t('time.hoursAgo', { count: diffHour });
  if (diffDay < 7) return i18n.t('time.daysAgo', { count: diffDay });

  return formatDate(d, { year: 'numeric', month: 'short', day: 'numeric' });
}
